import { useGetMentorFlags } from '@actions';
import { SortOrder } from '@components/V4/Table/Table';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { useAuthState } from '@contexts/AuthContext';
import { useQueryParamsState } from '@hooks';
import MyFlagsDesktopTable from '@modules/Mentor/MyFlags/MyFlagsDesktopTable';
import MyFlagsMobileCard from '@modules/Mentor/MyFlags/MyFlagsMobileCard';
import { TriState } from '@shared/clientTypes';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

const useQuery = () => new URLSearchParams(useLocation().search);

const MyFlags = () => {
  const { userId } = useAuthState();
  const query = useQuery();
  const limit = query.get('limit') || '10';
  const page = query.get('page') || '1';
  const sortBy = query.get('sortBy') || 'createdAt';
  const sortOrder = (query.get('sortOrder') || 'desc') as SortOrder;
  const [showActiveFlags, setShowActiveFlags] = useState<TriState>(
    (query.get('isActive') as TriState) || 'checked'
  );

  const [searchParams, setSearchParams] = useQueryParamsState<any>({
    mentorId: userId,
    page: page,
    limit: limit,
    sortBy: sortBy,
    sortOrder: sortOrder,
    isActive: showActiveFlags,
  });

  const { data: myFlags, isLoading, refetch } = useGetMentorFlags({ ...searchParams });

  const currentPageChangeHandler = (page: number) => {
    setSearchParams({
      ...searchParams,
      page,
    });
  };

  const pageSizeChangeHandler = (size: number) => {
    setSearchParams({
      ...searchParams,
      limit: size,
      page: 1,
    });
  };

  const pagination = {
    pageSize: limit !== null ? parseInt(limit) : 10,
    total: myFlags?.total,
    currentPage: page !== null ? parseInt(page) : 1,
    onCurrentPageChange: currentPageChangeHandler,
    onPageSizeChange: pageSizeChangeHandler,
  };

  return (
    <section className="laptop:ml-1 min-h-screen w-full pt-6 bg-surfaceBackground">
      <section className="max-w-[1564px] flex flex-col m-auto gap-6 p-4 tablet:p-6 laptop:px-24">
        <h1 className="flex justify-between font-raleway font-semibold text-2xl">My Flags</h1>

        <TriStateToggle
          title="Show Flags:"
          options={[
            { label: 'All', value: 'indeterminate' },
            { label: 'Active', value: 'checked' },
            { label: 'Closed', value: 'unchecked' },
          ]}
          value={showActiveFlags}
          onChange={(setValue) => {
            setShowActiveFlags(setValue);
            setSearchParams({
              ...searchParams,
              isActive: setValue,
              page: 1,
            });
          }}
          className="mt-3"
        />

        <section className="flex flex-col gap-6">
          <MyFlagsDesktopTable
            refetch={refetch}
            flags={myFlags?.flags}
            pagination={pagination}
            isLoading={isLoading}
          />
          <MyFlagsMobileCard
            refetch={refetch}
            flags={myFlags?.flags}
            pagination={pagination}
            isLoading={isLoading}
          />
        </section>
      </section>
    </section>
  );
};

export default MyFlags;
